import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { PagedData } from '@app/modules/lookup/models/paged-data.model';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { EnvironmentService } from "@app/core/services/environment.service";
import { BuySellHoursReportItem, BuySellPlan, BuySellPlanEmployee, BuySellPlanPublishHistory, BuySellPlanReviewer, BuySellPlanReviewerSubmit, BuySellPlanVerbose } from '../models/buysell.model';
import { api_routes } from '@app/consts';

const headers = new HttpHeaders({
    'Content-Type': 'application/json',
});

@Injectable({
    providedIn: 'root'
})
export class BuySellService {

    constructor(private http: HttpClient, private envService: EnvironmentService) { }

    /*
    .########..##.....##.##....##.....######..########.##.......##..........########..##..........###....##....##..######.
    .##.....##.##.....##..##..##.....##....##.##.......##.......##..........##.....##.##.........##.##...###...##.##....##
    .##.....##.##.....##...####......##.......##.......##.......##..........##.....##.##........##...##..####..##.##......
    .########..##.....##....##........######..######...##.......##..........########..##.......##.....##.##.##.##..######.
    .##.....##.##.....##....##.............##.##.......##.......##..........##........##.......#########.##..####.......##
    .##.....##.##.....##....##.......##....##.##.......##.......##..........##........##.......##.....##.##...###.##....##
    .########...#######.....##........######..########.########.########....##........########.##.....##.##....##..######.
    */

    getBuySellPlans(take?: number, skip?: number, filterString?: string, sort?: string): Observable<PagedData<BuySellPlan>> {
        let params = new HttpParams();
        take ? params = params.append('take', take) : null;
        skip ? params = params.append('skip', skip) : null;
        filterString ? params = params.append('Filter', filterString) : null;
        sort ? params = params.append('sort', sort) : null;

        return this.http
            .get<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.PLANS}`, {
                headers: headers,
                params: params
            })
    }

    getBuySellPlan(id: string): Observable<BuySellPlanVerbose> {
        return this.http
            .get<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.PLANS}/${id}`, {
                headers: headers
            })
    }

    deleteBuySellPlan(id: string): Observable<any> {
        return this.http
            .delete<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.PLANS}/${id}`, {
                headers: headers
            })
    }

    copyBuySellPlan(id: string): Observable<{ id: string }> {
        return this.http
            .post<{ id: string }>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.PLANS}/${id}/${api_routes.COPY}`, {}, {
                headers: headers
            })
    }

    publishBuySellPlan(id: string): Observable<any> {
        return this.http
            .post<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.PLANS}/${id}/${api_routes.PUBLISH}`, {}, {
                headers: headers
            })
    }

    getBuySellPlanStatuses(): Observable<PagedData<{ id: string, name: string }>> {
        return this.http
            .get<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/BuySellStatuses`, {
                headers: headers
            })
    }

    updateBuySellPlan(id: string, data: any): Observable<any> {
        return this.http
            .put<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.PLANS}/${id}`, data, {
                headers: headers
            })
    }

    createBuySellPlan(data: any): Observable<{ id: string }> {
        return this.http
            .post<{ id: string }>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.PLANS}`, data, {
                headers: headers
            })
    }

    getBuySellPlanEmployees(take?: number, skip?: number, filterString?: string, sort?: string, asOf?: string, include?: string): Observable<PagedData<BuySellPlanEmployee>> {
        let params = new HttpParams();
        take ? params = params.append('take', take) : null;
        skip ? params = params.append('skip', skip) : null;
        filterString ? params = params.append('Filter', filterString) : null;
        sort ? params = params.append('sort', sort) : null;
        asOf ? params = params.append('asOf', asOf) : null;
        include ? params = params.append('include', include) : null;

        return this.http
            .get<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.EMPLOYEES}`, {
                headers: headers,
                params: params
            })
    }

    getBuySellPlanEmployee(id: string): Observable<BuySellPlanEmployee> {
        return this.http
            .get<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.EMPLOYEES}/${id}`, {
                headers: headers
            })
    }

    addBuySellPlanEmployees(planId: string, addFilter: string): Observable<any> {
        let params = new HttpParams();
        planId ? params = params.append('plan', planId) : null;
        addFilter ? params = params.append('filter', addFilter) : null;

        return this.http
            .post<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.EMPLOYEES}`, {}, {
                headers: headers,
                params: params
            })
    }

    deleteBuySellPlanEmployee(id: string): Observable<any> {
        return this.http
            .delete<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.EMPLOYEES}/${id}`, {
                headers: headers
            })
    }

    getBuySellPlanReviewers(take?: number, skip?: number, filterString?: string, sort?: string, asOf?: string): Observable<PagedData<BuySellPlanReviewer>> {
        let params = new HttpParams();
        take ? params = params.append('take', take) : null;
        skip ? params = params.append('skip', skip) : null;
        filterString ? params = params.append('Filter', filterString) : null;
        sort ? params = params.append('sort', sort) : null;
        asOf ? params = params.append('asOf', asOf) : null;

        return this.http
            .get<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.PLAN_REVIEWERS}`, {
                headers: headers,
                params: params
            })
    }

    getBuySellPlanReviewer(id: string): Observable<BuySellPlanReviewer> {
        return this.http
            .get<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.PLAN_REVIEWERS}/${id}`, {
                headers: headers
            })
    }

    editBuySellPlanReviewer(id: string, data: BuySellPlanReviewerSubmit): Observable<any> {
        return this.http
            .put<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.PLAN_REVIEWERS}/${id}`, data, {
                headers: headers
            })
    }

    addBuySellPlanReviewer(data: BuySellPlanReviewerSubmit): Observable<{ id: string }> {
        return this.http
            .post<{ id: string }>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.PLAN_REVIEWERS}`, data, {
                headers: headers
            })
    }

    deleteBuySellPlanReviewer(id: string): Observable<any> {
        return this.http
            .delete<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.PLAN_REVIEWERS}/${id}`, {
                headers: headers
            })
    }

    getReviewerTypes(): Observable<PagedData<{ id: string, name: string }>> {
        return this.http
            .get<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}${api_routes.REVIEWER_TYPES}`, {
                headers: headers
            })
    }

    getBuySellPlanPublishHistory(take?: number, skip?: number, filter?: string, sort?: string, asOf?: string): Observable<PagedData<BuySellPlanPublishHistory>> {
        let params = new HttpParams();
        take ? params = params.append('take', take) : null;
        skip ? params = params.append('skip', skip) : null;
        filter ? params = params.append('filter', filter) : null;
        sort ? params = params.append('sort', sort) : null;
        asOf ? params = params.append('asOf', asOf) : null;

        return this.http
            .get<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.PLAN_PUBLISH_HISTORIES}`, {
                headers: headers,
                params: params
            })
    }

    async getAllBuySellPlanPublishHistory(sortString?: string, filterString?: string): Promise<PagedData<BuySellPlanPublishHistory>> {
        let take = 100;
        let skip = 0;
        let publishHistory = {
            skip: null,
            take: null,
            totalPages: null,
            total: null,
            data: []
        }

        while (publishHistory.total === null || skip < publishHistory.total) {
            const res = await this.getBuySellPlanPublishHistory(take, skip, filterString, sortString).toPromise();

            if (res && res.data && res.data.length > 0) {
                publishHistory.data = publishHistory.data.concat(res.data);
                skip += take;
                publishHistory.total = res.total;
            } else {
                break;
            }
        }

        return publishHistory;
    }

    /*
    .########..##.....##.##....##.....######..########.##.......##.............###....########..########..########...#######..##.....##....###....##........######.
    .##.....##.##.....##..##..##.....##....##.##.......##.......##............##.##...##.....##.##.....##.##.....##.##.....##.##.....##...##.##...##.......##....##
    .##.....##.##.....##...####......##.......##.......##.......##...........##...##..##.....##.##.....##.##.....##.##.....##.##.....##..##...##..##.......##......
    .########..##.....##....##........######..######...##.......##..........##.....##.########..########..########..##.....##.##.....##.##.....##.##........######.
    .##.....##.##.....##....##.............##.##.......##.......##..........#########.##........##........##...##...##.....##..##...##..#########.##.............##
    .##.....##.##.....##....##.......##....##.##.......##.......##..........##.....##.##........##........##....##..##.....##...##.##...##.....##.##.......##....##
    .########...#######.....##........######..########.########.########....##.....##.##........##........##.....##..#######.....###....##.....##.########..######.
    */

    getBuySellApprovals(take?: number, skip?: number, filterString?: string, sort?: string): Observable<PagedData<any>> {
        let params = new HttpParams();
        take ? params = params.append('take', take) : null;
        skip ? params = params.append('skip', skip) : null;
        filterString ? params = params.append('Filter', filterString) : null;
        sort ? params = params.append('sort', sort) : null;

        return this.http
            .get<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.APPROVALS}`, {
                headers: headers,
                params: params
            })
    }

    getBuySellApproval(id: string): Observable<any> {
        // return of(
        //     {
        //         id: "23463456",
        //         employee: {
        //             "id": "usr_Q58GDL7caiRl0A",
        //             "firstName": "Fintan",
        //             "lastName": "Senior Front End"
        //         },
        //         orderNumber: 1,
        //         isApproved: true,
        //         comments: "Approved",
        //         plan: {
        //             id: "9842056478",
        //             name: "Plan 1"
        //         },
        //         status: {
        //             id: "PENDING_APPROVAL",
        //             text: "Pending Approval"
        //         },
        //         reviewer: {
        //             "id": "usr_Q58GDL7caiRl0A",
        //             "firstName": "DO NOT CHANGEs",
        //             "lastName": "(: Assistant"
        //         },
        //         buySell: {
        //             type: {
        //                 id: "buy",
        //                 name: "Buy"
        //             },
        //             hours: 12,
        //             hourlyCost: 100
        //         },
        //         version: {
        //             "createdOn": "2025-10-08T20:51:54.8831043+00:00",
        //             "modifiedList": null,
        //             "changeReason": null,
        //             "comments": null,
        //             "createdBy": {
        //                 "id": "hubbub_o0ekUYl2rWvwUV",
        //                 "firstName": "Hubbub",
        //                 "lastName": "HR"
        //             }
        //         }
        //     }
        // );

        return this.http
            .get<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.APPROVALS}/${id}`, {
                headers: headers
            })
    }

    submitBuySellRequest(id: string, request: { hours: number, type: string }): Observable<any> {
        return this.http
            .post<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.EMPLOYEES}/${id}/${api_routes.REQUEST}`, request, {
                headers: headers
            })
    }

    approveBuySellApproval(id: string, comments: { comments: string }): Observable<any> {
        return this.http
            .post<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.APPROVALS}/${id}/${api_routes.APPROVE}`, comments, {
                headers: headers
            })
    }

    denyBuySellApproval(id: string, comments: { comments: string }): Observable<any> {
        return this.http
            .post<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.APPROVALS}/${id}/${api_routes.DENY}`, comments, {
                headers: headers
            })
    }

    getBuySellApprovalStatuses(): Observable<PagedData<{ id: string, name: string }>> {
        return this.http
            .get<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.BUY_SELL_APPROVAL_STATUSES}`, {
                headers: headers
            })
    }

    /*
    .########..##.....##.##....##.....######..########.##.......##..........########..########.########...#######..########..########..######.
    .##.....##.##.....##..##..##.....##....##.##.......##.......##..........##.....##.##.......##.....##.##.....##.##.....##....##....##....##
    .##.....##.##.....##...####......##.......##.......##.......##..........##.....##.##.......##.....##.##.....##.##.....##....##....##......
    .########..##.....##....##........######..######...##.......##..........########..######...########..##.....##.########.....##.....######.
    .##.....##.##.....##....##.............##.##.......##.......##..........##...##...##.......##........##.....##.##...##......##..........##
    .##.....##.##.....##....##.......##....##.##.......##.......##..........##....##..##.......##........##.....##.##....##.....##....##....##
    .########...#######.....##........######..########.########.########....##.....##.########.##.........#######..##.....##....##.....######.
    */

    async getAllBuySellHoursReportData(filter?: string, sort?: string, asOf?: string, include?: string): Promise<PagedData<BuySellHoursReportItem>> {
        let take = 100;
        let skip = 0;
        let allData = {
            skip: null,
            take: null,
            totalPages: null,
            total: null,
            data: []
        }

        while (allData.total === null || skip < allData.total) {
            const res = await this.getBuySellHoursReportData(take, skip, filter, sort, asOf, include).toPromise();

            if (res && res.data && res.data.length > 0) {
                allData.data = allData.data.concat(res.data);
                skip += take;
                allData.total = res.total;
            } else {
                break;
            }
        }

        return allData;
    }

    getBuySellHoursReportData(take?: number, skip?: number, filter?: string, sort?: string, asOf?: string, include?: string): Observable<PagedData<BuySellHoursReportItem>> {
        let params = new HttpParams();
        take ? params = params.append('take', take) : null;
        skip ? params = params.append('skip', skip) : null;
        filter ? params = params.append('filter', filter) : null;
        sort ? params = params.append('sort', sort) : null;
        asOf ? params = params.append('asOf', asOf) : null;
        include ? params = params.append('include', include) : null;

        return this.http
            .get<any>(`${this.envService.env.apiUrl}${api_routes.BUY_SELL}/${api_routes.REPORTS}/HoursReport`, {
                headers: headers,
                params: params
            })
    }

}